import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  useRecurringCosts,
  useLinkExpenseToRecurringCost,
} from "@/features/finances/hooks/useFinances";
import type { EmployeeExpense } from "@/features/finances/types";
import { formatAmount } from "@/utils/formatAmount";
import { MonthlyPeriodSelect, isPeriodInRange, formatRange } from "./MonthlyPeriodSelect";

interface LinkRecurringCostModalProps {
  expense: EmployeeExpense;
  onClose: () => void;
}

/** Pull the first human-readable message out of a wrapped/unwrapped DRF error. */
function firstError(e: unknown): string | null {
  const data = (e as { response?: { data?: unknown } })?.response?.data as
    | { errors?: unknown }
    | undefined;
  const errors = (data?.errors ?? data) as unknown;
  if (!errors) return null;
  if (typeof errors === "string") return errors;
  if (typeof errors === "object") {
    const val = Object.values(errors as Record<string, unknown>)[0];
    if (Array.isArray(val)) return String(val[0]);
    if (typeof val === "string") return val;
  }
  return null;
}

/** Attach an ALREADY FILED invoice to a recurring cost as its month's file.
 *
 *  The counterpart of AddMonthlyFileModal (which uploads a NEW file from the
 *  recurring-cost panel): here the invoice already exists — typically submitted
 *  as a standalone expense and only afterwards recognised as a monthly instance.
 */
export function LinkRecurringCostModal({ expense, onClose }: LinkRecurringCostModalProps) {
  const now = new Date();
  const [costId, setCostId] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [err, setErr] = useState<string | null>(null);

  const { data: recurringCosts = [] } = useRecurringCosts();
  const linkMutation = useLinkExpenseToRecurringCost();
  const activeCosts = recurringCosts.filter((c) => c.is_active);
  const cost = activeCosts.find((c) => c.id === costId) ?? null;
  const inRange = !cost || isPeriodInRange(year, month, cost.start_date, cost.end_date);
  // An accepted invoice already produced its own one-time cost; the server drops it
  // on linking, because from now on the amount is counted through the cost's average.
  const wasAccepted = expense.status !== "pending" && expense.status !== "rejected";

  const submit = async () => {
    if (!costId) {
      setErr("Wybierz koszt stały.");
      return;
    }
    if (!inRange) {
      setErr(`Miesiąc musi mieścić się w okresie kosztu (${formatRange(cost!.start_date, cost!.end_date)}).`);
      return;
    }
    try {
      await linkMutation.mutateAsync({
        id: expense.id,
        recurring_cost: costId,
        period_year: year,
        period_month: month,
      });
      onClose();
    } catch (e) {
      setErr(firstError(e) ?? "Błąd podczas podpinania faktury.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Podepnij pod koszt stały
        </h3>
        <div className="mb-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
            {expense.description} · {formatAmount(expense.amount)} {expense.currency}
          </p>
          {expense.currency !== "PLN" && expense.amount_pln == null && (
            // Fixed costs are settled in PLN, so the amount is converted on attach.
            <p className="text-[11px] text-gray-400 mt-0.5">
              Kwota zostanie przeliczona na PLN po najnowszym kursie NBP.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Koszt stały *
            </label>
            <SearchableSelect
              placeholder="— wybierz koszt stały —"
              value={costId}
              onChange={(e) => setCostId(e.target.value)}
              options={activeCosts.map((c) => ({
                value: c.id,
                label: `${c.description}${c.project_name ? ` · ${c.project_name}` : ""}`,
              }))}
            />
            {cost && (
              <p className="text-[11px] text-gray-400 mt-1">
                Okres kosztu: {formatRange(cost.start_date, cost.end_date)}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Miesiąc *
            </label>
            <MonthlyPeriodSelect
              year={year}
              month={month}
              onChange={(y, m) => { setYear(y); setMonth(m); }}
            />
            {!inRange && cost && (
              <p className="text-xs text-red-600 mt-1">
                Poza okresem kosztu ({formatRange(cost.start_date, cost.end_date)}).
              </p>
            )}
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
            Faktura zacznie liczyć się jako miesięczna kwota tego kosztu (średnia z jego faktur)
            {wasAccepted && ", a koszt jednorazowy z akceptacji zostanie usunięty, żeby ta sama kwota nie policzyła się dwa razy"}.
          </p>
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={linkMutation.isPending}
              onClick={submit}
              disabled={!costId || !inRange}
            >
              Podepnij
            </Button>
            <Button size="sm" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
